export const USER_ROLES = ['USER', 'GLOBAL_ADMIN'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const USER_ROLE_LABELS: Readonly<Record<UserRole, string>> = {
  USER: 'User',
  GLOBAL_ADMIN: 'Global Admin',
};

export const DOMAINS = [
  'EXCESS_RETURN',
  'EXCESS_RETURN_ADMIN',
  'SASI',
  'USER_MANAGEMENT',
  'CENTRAL_ROUTING',
  'OPERATIONS',
  'STOCKLOCATOR',
] as const;
export type Domain = (typeof DOMAINS)[number];

export const DATA_SCOPE_MODES = ['none', 'restricted', 'exclude', 'all'] as const;
export type DataScopeMode = (typeof DATA_SCOPE_MODES)[number];

export interface UserDataScope {
  readonly plantMode: DataScopeMode;
  readonly importerMode: DataScopeMode;
  readonly plantIds: readonly string[];
  readonly importerPrefixes: readonly string[];
}

export const NO_DATA_SCOPE: UserDataScope = {
  plantMode: 'none',
  importerMode: 'none',
  plantIds: [],
  importerPrefixes: [],
};

export function isPlantId(value: string): boolean {
  return /^[0-9]+$/.test(value);
}

export function isImporterPrefix(value: string): boolean {
  return /^[1-9][0-9]{2}$/.test(value);
}

export function normalizeDataScope(value: unknown): UserDataScope {
  if (!value || typeof value !== 'object') {
    return NO_DATA_SCOPE;
  }
  const validMode = (mode: unknown): mode is DataScopeMode =>
    mode === 'none' || mode === 'restricted' || mode === 'exclude' || mode === 'all';
  let plantMode: DataScopeMode;
  let importerMode: DataScopeMode;
  if ('mode' in value) {
    if (
      'plantMode' in value ||
      'importerMode' in value ||
      !validMode(value.mode) ||
      value.mode === 'exclude'
    ) {
      return NO_DATA_SCOPE;
    }
    plantMode = importerMode = value.mode;
  } else {
    if (
      !('plantMode' in value) ||
      !validMode(value.plantMode) ||
      !('importerMode' in value) ||
      !validMode(value.importerMode)
    ) {
      return NO_DATA_SCOPE;
    }
    plantMode = value.plantMode;
    importerMode = value.importerMode;
  }
  // Never turn a malformed exclusion list into an unrestricted grant.
  const validList = (items: unknown, valid: (item: string) => boolean): boolean =>
    Array.isArray(items) && items.every((item) => typeof item === 'string' && valid(item.trim()));
  if (
    (plantMode === 'exclude' &&
      !validList('plantIds' in value ? value.plantIds : null, isPlantId)) ||
    (importerMode === 'exclude' &&
      !validList('importerPrefixes' in value ? value.importerPrefixes : null, isImporterPrefix))
  ) {
    return NO_DATA_SCOPE;
  }
  const normalizeList = (items: unknown, valid: (item: string) => boolean): string[] =>
    Array.isArray(items)
      ? [
          ...new Set(
            items
              .filter((item): item is string => typeof item === 'string')
              .map((item) => item.trim())
              .filter(valid),
          ),
        ]
      : [];
  return {
    plantMode,
    importerMode,
    plantIds: normalizeList('plantIds' in value ? value.plantIds : [], isPlantId),
    importerPrefixes: normalizeList(
      'importerPrefixes' in value ? value.importerPrefixes : [],
      isImporterPrefix,
    ),
  };
}

export function dataScopeAllows(
  mode: DataScopeMode,
  values: readonly string[],
  target: string,
): boolean {
  return (
    mode === 'all' ||
    (mode === 'restricted' && values.includes(target)) ||
    (mode === 'exclude' && !values.includes(target))
  );
}

export function dataScopeDescription(mode: DataScopeMode, values: readonly string[]): string {
  switch (mode) {
    case 'none':
      return 'none';
    case 'all':
      return 'all';
    case 'restricted':
      return values.join(', ') || 'none';
    case 'exclude':
      return values.length ? `all except ${values.join(', ')}` : 'all';
  }
}

export interface UserAccessProfile {
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
  readonly role: UserRole;
  readonly domains: readonly Domain[];
  readonly dataScope: UserDataScope;
}

export function normalizeUserAccessProfile(user: UserAccessProfile): UserAccessProfile {
  return { ...user, dataScope: normalizeDataScope(user.dataScope) };
}

export const DOMAIN_LABELS: Readonly<Record<Domain, string>> = {
  EXCESS_RETURN: 'Excess Return',
  EXCESS_RETURN_ADMIN: 'Excess Return Admin',
  SASI: 'SASI',
  USER_MANAGEMENT: 'User Management',
  CENTRAL_ROUTING: 'Central Routing',
  OPERATIONS: 'Operations',
  STOCKLOCATOR: 'Stocklocator',
};

export function isGlobalAdmin(user: UserAccessProfile | null): boolean {
  return user?.role === 'GLOBAL_ADMIN';
}

export function canAccessDomain(user: UserAccessProfile | null, domain: Domain): boolean {
  return isGlobalAdmin(user) || user?.domains.includes(domain) === true;
}
